import { ALLOW_MOTIVATION_EVENT_LIST } from '@/helpers/constants'
import { dateProvider } from '@/helpers/date'
import { getAgeDecade, getAgeDecadeNumber } from '@/helpers/utils'

export default function textInterpolations(model, $t) {
	const ctx = {
		...model,
		$t,
	}

	return {
		weightLossGoalsFirstOption: () => {
			switch (ctx.weightUnitSystem) {
				case 'lbs':
					return `1-20 ${ctx.$t('common.lbs')}`
				case 'kg':
					return `1-10 ${ctx.$t('common.kg')}`
				case 'st':
					return `1-1,5 ${ctx.$t('common.st')}`
				default:
					return `1-10 ${ctx.$t('common.kg')}`
			}
		},
		weightLossGoalsSecondOption: () => {
			switch (ctx.weightUnitSystem) {
				case 'lbs':
					return `21-50 ${ctx.$t('common.lbs')}`
				case 'kg':
					return `11-20 ${ctx.$t('common.kg')}`
				case 'st':
					return `2-3 ${ctx.$t('common.st')}`
				default:
					return `11-20 ${ctx.$t('common.kg')}`
			}
		},
		weightLossGoalsThirdOption: () => {
			switch (ctx.weightUnitSystem) {
				case 'lbs':
					return `50 ${ctx.$t('common.lbs')}`
				case 'kg':
					return `20 ${ctx.$t('common.kg')}`
				case 'st':
					return `4+ ${ctx.$t('common.st')}`
				default:
					return `20 ${ctx.$t('common.kg')}`
			}
		},
		motivationalEventTitle: () => {
			const sourceProperty = ctx.motivation_event?.[0] || null
			const eventList = ALLOW_MOTIVATION_EVENT_LIST
			const eventIsValid = sourceProperty && eventList.includes(sourceProperty)
			const event = eventIsValid ? sourceProperty : 'other'

			if (event === 'baby_weight') {
				return ctx.$t('pages.when_is_the_event.title.baby_weight')
			}

			if (event === 'school') {
				return ctx.$t('pages.when_is_the_event.title.reunion')
			}

			return ctx.$t(`pages.when_is_the_event.title.${event}`)
		},
		motivationalEventTitleEventPersonalization: () => {
			const sourceProperty = ctx.motivation_event?.[0] || null
			const eventList = ALLOW_MOTIVATION_EVENT_LIST
			const eventIsValid = sourceProperty && eventList.includes(sourceProperty)
			const event = eventIsValid ? sourceProperty : 'other'

			if (event === 'baby_weight') {
				return ctx.$t('pages.when_is_the_event.title.baby_weight')
			}

			if (event === 'school') {
				return ctx.$t('pages.when_is_the_event.title.reunion')
			}

			if (event === 'wedding') {
				return ctx.$t('pages.when_is_the_event.title.wedding_v2')
			}

			return ctx.$t(`pages.when_is_the_event.title.${event}`)
		},
		gender: () => (ctx.sex === 'male' ? ctx.$t('common.men') : ctx.$t('common.women')).toLowerCase(),
		genderUppercase: () => (ctx.sex === 'male' ? ctx.$t('common.men') : ctx.$t('common.women')),
		genderNeutralUppercase: () => {
			return ctx.non_binary_identity !== 'non_binary'
				? ctx.sex === 'male'
					? ctx.$t('common.men')
					: ctx.$t('common.women')
				: ctx.$t('common.people')
		},
		usersCountByGenderTitle: () => {
			ctx.sex === 'male'
				? ctx.$t('pages.processing.male_title', {
						age: this.$t(`pages.processing.age_${getAgeDecadeNumber(ctx.age)}`),
					})
				: ctx.$t('pages.processing.female_title', {
						age: this.$t(`pages.processing.age_${getAgeDecadeNumber(ctx.age)}`),
					})
		},
		ageDecade: () => ctx.$t(`pages.additional-question-1.age.${getAgeDecade(ctx.age)}`),
		constitution: () => ctx.constitution ?? 'your',
		ageDecadeWords: () => ctx.$t(`pages.additional-question-2.age.${getAgeDecade(ctx.age)}`),
		ageNumber: () => String(Array.isArray(ctx.age) ? ctx.age[0] : ctx.age),
		ageNumberPlus: () => `${ctx.age}+`,
		userName: () => String(ctx.userName || ''),
		dreamBodyText: () => String(ctx.dreamBodyText || ''),
		targetWeightRange: () => {
			const weight = ctx.targetWeight
			let modifier = 1

			if (ctx.weightUnitSystem === 'kg') {
				modifier = 10
			}
			if (ctx.weightUnitSystem === 'lbs') {
				modifier = 20
			}

			return `${weight - modifier + '-' + (weight + modifier)} ${ctx.$t(`common.${ctx.weightUnitSystem}`)}`
		},
		targetWeight: () => `${ctx.targetWeight} ${ctx.$t(`common.${ctx.weightUnitSystem}`)}`,
		/**
		 * @deprecated
		 * Instead use prediction date with steps to show appropriate predict date explicitly.
		 * this function is now hardcoded to show step 2 predict
		 * @returns {string}
		 */
		predictionDate: () => {
			if (!ctx.dateEnd) {
				return ''
			}
			return dateProvider.toLongDate(new Date(ctx.dateEnd))
		},
		predictionDate_1: () => {
			if (!ctx.weightPredictDate_1) {
				return ''
			}
			return dateProvider.toLongDate(new Date(ctx.weightPredictDate_1))
		},
		predictionDate_2: () => {
			if (!ctx.weightPredictDate_2) {
				return ''
			}
			return dateProvider.toLongDate(new Date(ctx.weightPredictDate_2))
		},
		predictionDate_3: () => {
			if (!ctx.weightPredictDate_3) {
				return ''
			}
			return dateProvider.toLongDate(new Date(ctx.weightPredictDate_3))
		},
		predictionDate_4: () => {
			if (!ctx.weightPredictDate_4) {
				return ''
			}
			return dateProvider.toLongDate(new Date(ctx.weightPredictDate_4))
		},
		predictionDate_4_short: () => {
			if (!ctx.weightPredictDate_4) {
				return ''
			}
			return dateProvider.toFullMonthDay(new Date(ctx.weightPredictDate_4))
		},
		weightDelta: () => {
			if (!ctx.weight || !ctx.targetWeight) {
				return ''
			}
			return `${ctx.currentWeight - ctx.targetWeight} ${ctx.$t(`common.${ctx.weightUnitSystem}`)}`
		},
		safeWeightLoss: () => {
			if (!ctx.weight) {
				return ''
			}
			const minSafeWeightLoss = Math.floor((ctx.weight.value * 5) / 100)
			const MIN_VALUE = 1

			if (minSafeWeightLoss > MIN_VALUE) {
				switch (ctx.weightUnitSystem) {
					case 'lbs':
						return `${minSafeWeightLoss} ${ctx.$t('common.lbs')}`
					case 'kg':
						return `${minSafeWeightLoss} ${ctx.$t('common.kg')}`
					case 'st':
						return `${minSafeWeightLoss} ${ctx.$t('common.st')}`
					default:
						return `${minSafeWeightLoss} ${ctx.$t('common.kg')}`
				}
			}

			return ''
		},
		goalPersonalizationString: () => {
			const goal = ctx.main_goal[0]

			if (goal === 'lose_weight') {
				return ctx.$t('common.goal_personalization.lose_weight')
			}
			if (goal === 'be_healthy') {
				return ctx.$t('common.goal_personalization.be_healthy')
			}
			if (goal === 'get_active') {
				return ctx.$t('common.goal_personalization.get_active')
			}

			return ctx.$t('common.goal_personalization.other')
		},

		workoutIntensity: () => {
			const workoutLevel = ctx.workout_level[0]

			if (workoutLevel === 'easy') {
				return 'Low-intensity workout'
			}

			if (workoutLevel === 'hard') {
				return 'High-intensity workout'
			}

			// By default, we return the medium level
			return 'Moderate-intensity workout'
		},
	}
}
